import React from "react";
import store from "../Store/index";
import { apiStart, apiSuccess, apiFailure } from "../Store/dataSlice";
import axiosInstance from "./axiosInstance";
import { toastify } from "../components/Toast";

async function callApi(
  key,
  endpoint,
  method = "GET",
  body = null,
  headers = {},
  customSuccessMsg = undefined
) {
  const dispatch = store.dispatch;
  try {
    dispatch(apiStart(key));
    const config = {
      url: endpoint,
      method,
      headers,
    };

    if (body) {
      config.data = body;
    }

    const response = await axiosInstance(config);

    dispatch(apiSuccess({ key, data: response.data }));
    if (method.toUpperCase() !== "GET") {
      if (customSuccessMsg) {
        toastify("success", customSuccessMsg);
      } else if (customSuccessMsg !== false && response.data?.message) {
        toastify("success", response.data.message);
      }
    }
    return response.data;
  } catch (err) {
    const errorMsg = err.response?.data?.message || err.message;
    dispatch(apiFailure({ key, error: errorMsg }));
    toastify("error", errorMsg);
    throw err;
  }
}

// Define API methods here
const services = {
  getBrands: () => callApi("brands", "brands", "GET"),
  getCharacters: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return callApi("characters", query ? `/characters?${query}` : "/characters", "GET");
  },
  deleteBrand: (id) => callApi("deleteBrand", `brands/${id}`, "DELETE"),
  duplicateBrand: (body) =>
    callApi("deleteBrand", `brands/duplicate-brand`, "POST", body),
  imageGeneration: (body) =>
    callApi("imageGeneration", `ads/image/variation`, "POST", body, {}, <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Generating images…<br />Come back later if you’d like.</div>),
  createCampaign: (body) =>
    callApi("createCampaign", `/ads/create-campaign`, "POST", body),
  getCampaigns: () => callApi("getCampaigns", `/ads/campaigns`, "GET"),
  pauseResumeCampaign: (id) =>
    callApi("pauseResumeCampaign", `/ads/campaigns/${id}/toggle`, "POST"),
  deleteCampaign: (id) =>
    callApi("deleteCampaign", `/ads/campaigns/${id}`, "DELETE"),
  updateCampaign: (id, body) =>
    callApi("updateCampaign", `/ads/campaigns/${id}`, "PATCH", body),
  testCampaign: (id) =>
    callApi("testCampaign", `/ads/campaigns/${id}/test`, "POST"),
  editVariation: (body) =>
    callApi("editVariation", `/ads/edit-image/variation`, "POST", body),
  createCharacter: (body) =>
    callApi("customCharacter", `/characters/create-custom`, "POST", body, {}, false),
  getCharacterById: (id) => callApi("getCharacter", `/characters/${id}`, "GET"),
  deleteCharacter: (id) =>
    callApi("deleteCharacter", `/characters/${id}`, "DELETE"),
  deleteMultipleCharacters: (characterIds) =>
    callApi("deleteMultipleCharacters", `/characters`, "DELETE", { characterIds }),
  uploadImage: (body, customSuccessMsg = undefined) =>
    callApi("uploadImage", `/assets/upload`, "POST", body, {
      "Content-Type": "multipart/form-data",
    }, customSuccessMsg),
  editCharacter: (id, body) =>
    callApi("editCharacter", `/characters/${id}/edit`, "POST", body, {}, false),
  getAllVoices: () => callApi("getAllVoices", `/voices`, "GET"),
  saveCharacter: (id, body) =>
    callApi("saveCharacter", `/characters/${id}`, "PATCH", body),
  studioVoices: (body) =>
    callApi("studioVoices", `/voices/suggest`, "POST", body),
  generateAudio: (body) =>
    callApi("generateAudio", `/voices/generate-audio`, "POST", body),
  generateVideo: (body) =>
    callApi("generateVideo", `/video-ads/generate`, "POST", body, {
      "Content-Type": "multipart/form-data",
    }, <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Generating videos…<br />Come back later if you’d like.</div>),
  generateScript: (body) =>
    callApi("generateScript", `/brands/generate-script`, "POST", body),
  generateMusic: (body) =>
    callApi("generateMusic", `/voices/generate-music`, "POST", body),
  generateSoundEffect: (body) =>
    callApi("generateSoundEffect", `/voices/generate-sound-effect`, "POST", body),
  generateCaptions: (id) =>
    callApi("generateCaptions", `/video-ads/${id}/generate-captions`, "POST"),
  getEditorTimeline: (id) =>
    callApi("getEditorTimeline", `/video-ads/${id}/editor-timeline`, "GET"),
  saveEditorTimeline: (id, body) =>
    callApi("saveEditorTimeline", `/video-ads/${id}/editor-timeline`, "POST", body),
  saveEditorProgress: (id, body) =>
    callApi("saveEditorProgress", `/video-ads/${id}/save-editor-progress`, "POST", body),
  getAssets: () => callApi("getAssets", "/assets", "GET"),
  getMusicAssets: (params) => callApi("getMusicAssets", "/assets/music?" + new URLSearchParams(params).toString(), "GET"),
  deleteMusicAssets: (musicIds) => callApi("deleteMusicAssets", "/assets/music", "DELETE", { musicIds }),
  deleteMultipleAssets: (assetIds) =>
    callApi("deleteMultipleAssets", "/assets", "DELETE", { assetIds }),
  deleteMultipleAds: (adIds) =>
    callApi("deleteMultipleAds", "/ads/delete-multiple", "POST", { adIds }),
  deleteMultipleVideoAds: (videoAdIds) =>
    callApi("deleteMultipleVideoAds", "video-ads", "DELETE", { videoAdIds }),
  getVideoAdById: (id) => callApi("getVideoAdById", `/video-ads/${id}`, "GET"),
  saveVariation: (videoAdId, body) =>
    callApi("saveVariation", `/video-ads/${videoAdId}/variations`, "POST", body),
  renameVariation: (videoAdId, variationId, body) =>
    callApi("renameVariation", `/video-ads/${videoAdId}/variations/${variationId}`, "PATCH", body),
  deleteVariation: (videoAdId, variationId) =>
    callApi("deleteVariation", `/video-ads/${videoAdId}/variations/${variationId}`, "DELETE"),
  createTopupCheckout: (credits) =>
    callApi("createTopupCheckout", "/subscriptions/create-topup-checkout", "POST", { credits }),
  getPlansList: () => callApi("plansList", "plans/list", "GET"),
  activateOrUpdateSubscription: (body) =>
    callApi("activateOrUpdateSubscription", "/subscriptions/activate-or-update", "POST", body),
  getCMOList: () => callApi("cmoList", "/cmo/list", "GET"),
  deleteCMO: (id) => callApi("deleteCMO", `/cmo/${id}`, "DELETE", null, {}, "Project deleted successfully"),
  updateBrandProfile: (id, body) => callApi("updateBrandProfile", `/cmo/update-brand-profile/${id}`, "PUT", body),
  generateMarketingPlan: (body) => callApi("generateMarketingPlan", "/cmo/generate-marketing-plan", "POST", body),
  generateAdConcepts: (body) => callApi("generateAdConcepts", "/cmo/generate-ad-concepts", "POST", body),
  generateCampaignConcepts: (body) => callApi("generateCampaignConcepts", "/cmo/generate-campaign-concepts", "POST", body),
  regenerateConcept: (body) => callApi("regenerateConcept", "/cmo/regenerate-concept", "POST", body),
  editAdConcept: (body) => callApi("editAdConcept", "/cmo/ad-concept", "PUT", body),
  generateScriptFromConcept: (body) => callApi("generateScriptFromConcept", "/cmo/regenerate-script", "POST", body),
  createImageAd: (brandId, body) => callApi("createImageAd", `/ads/create/${brandId}`, "POST", body, { "Content-Type": "multipart/form-data" }, <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Generating images…<br />Come back later if you’d like.</div>),
  exportVideo: (videoAdId, body) =>
    callApi("exportVideo", `/video-ads/${videoAdId}/export`, "POST", body, {}, <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Exporting videos…<br />Come back later if you’d like.</div>),
  getExportStatus: (videoAdId, jobId) =>
    callApi("getExportStatus", `/video-ads/${videoAdId}/export/${jobId}`, "GET"),
};

export default services;
